let jsQrLoadPromise: Promise<void> | undefined;

function loadJsQr(): Promise<void> {
  if (window.jsQR) return Promise.resolve();
  if (jsQrLoadPromise) return jsQrLoadPromise;
  jsQrLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    const productionPath = "../js/jsQR.js";
    script.src = import.meta.env.DEV
      ? new URL("/js/jsQR.js", window.location.origin).href
      : new URL(productionPath, import.meta.url).href;
    script.async = true;
    script.onload = () => window.jsQR ? resolve() : reject(new Error("jsQRの初期化に失敗しました。"));
    script.onerror = () => reject(new Error(`jsQRを読み込めませんでした: ${script.src}`));
    document.head.append(script);
  }).catch((error) => {
    jsQrLoadPromise = undefined;
    throw error;
  });
  return jsQrLoadPromise;
}

export async function scanQrFromCamera(video: HTMLVideoElement, signal: AbortSignal): Promise<string> {
  await loadJsQr();
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("このブラウザではカメラを利用できません。");

  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      width: { ideal: 640 },
      height: { ideal: 480 },
      facingMode: "environment",
    },
    audio: false,
  });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    stream.getTracks().forEach((track) => track.stop());
    throw new Error("QR読取用Canvasを初期化できませんでした。");
  }

  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  try {
    if (signal.aborted) throw new DOMException("QR reading cancelled", "AbortError");
    await new Promise<void>((resolve, reject) => {
      if (video.readyState >= 1) return resolve();
      const onMetadata = () => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      };
      const onAbort = () => {
        video.removeEventListener("loadedmetadata", onMetadata);
        reject(new DOMException("QR reading cancelled", "AbortError"));
      };
      video.addEventListener("loadedmetadata", onMetadata, { once: true });
      signal.addEventListener("abort", onAbort, { once: true });
    });
    await video.play();

    return await new Promise<string>((resolve, reject) => {
      const finishAbort = () => {
        window.clearInterval(interval);
        reject(new DOMException("QR reading cancelled", "AbortError"));
      };
      const interval = window.setInterval(() => {
        if (!video.videoWidth || !video.videoHeight || video.paused) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        try {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const image = context.getImageData(0, 0, canvas.width, canvas.height);
          const result = window.jsQR?.(image.data, image.width, image.height);
          if (!result) return;
          window.clearInterval(interval);
          signal.removeEventListener("abort", finishAbort);
          resolve(Array.from(result.matrix.data, (value) => value ? "1" : "0").join(""));
        } catch {
          // カメラのフレーム切替中は次のフレームで再試行する。
        }
      }, 500);
      signal.addEventListener("abort", finishAbort, { once: true });
    });
  } finally {
    video.pause();
    video.srcObject = null;
    stream.getTracks().forEach((track) => track.stop());
  }
}
